import { ICartLineItem } from "interfaces";
import { useTranslation } from "next-i18next";
import { FC, useState, ChangeEvent, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Link from "next/link";
import { formatNetWeight } from "@Components/cart/helpers/helpers";
import {
  GetPerWeightPrice,
  GetPerWeightPriceCA,
} from "@Components/cartLineItem/helpers/helpers";
import { Button, TextField } from "@Components/ui";
import { MEMBERSHIP_CONSTANTS } from "@Constants/common";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { getItem, setItem } from "@Utils/browserStorage";
import {
  cartUtilQtyOnBlurHandler,
  cartUtilQtyRemove,
  cartUtilQtyUpdate,
  cartUtilRemoveItem,
} from "@Utils/cartUtils";
import { convertToDollar } from "@Utils/currency";
import { currency } from "@Utils/index";
import { isCanadianLocale } from "@Utils/locale";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import Image from "components/Image";
import classes from "./CartLineItem.module.scss";

interface Props {
  item: ICartLineItem;
  isCart?: boolean;
  isReadOnly: boolean;
  className?: string;
  isFlyOut: boolean;
  isToast?: boolean;
}

const CartLineItem: FC<Props> = ({
  item,
  isCart,
  isReadOnly,
  className,
  isFlyOut,
  isToast,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useTranslation("common");
  const { lineItems } = useAppSelector((state) => state.cart);
  const [quantityValue, setQuantityValue] = useState(item?.quantity);
  const { quantity = 0 } = item;
  const [prevValue, setPrevValue] = useState(item?.quantity);

  //console.log("###","item",quantity);
  //console.log("###","redux",lineItems)

  useEffect(() => {
    setQuantityValue(quantity);
    setPrevValue(quantity);
  }, [quantity]);

  const joinClass = isToast
    ? "flyout"
    : isCart || isFlyOut
    ? "cart"
    : "checkout";

  const currentLocale = useAppSelector(localeSelector)?.languageCode ?? "en-US";
  const weight = Number(item.weightText?.replace("kilograms", ""));
  const perUnitPrice = Number(item.perUnitPriceText);
  const isFixedPrice = item.isFixedPrice;
  const UOM =
    item?.weightText?.includes("LB") || item?.weightText?.includes("pounds")
      ? "LB"
      : item?.weightText?.includes("KG") ||
        item?.weightText?.includes("kilograms")
      ? "KG"
      : "";
  const weightText = formatNetWeight(weight, UOM, currentLocale);
  const unitPrice = GetPerWeightPrice(perUnitPrice, UOM); // uncomment if CAN needs KG display too.
  const unitPriceCA = GetPerWeightPriceCA(
    perUnitPrice * weight,
    weight,
    UOM,
    currentLocale
  );
  const totalPrice = isCanadianLocale(currentLocale)
    ? item?.rawTotalPrice ?? 0
    : item?.totalPrice ?? 0;
  const isMobile = useMediaQuery("(max-width:60em)");
  const isMobileSmall = useMediaQuery("(max-width:30em)");
  const priceEmphasisClass =
    isToast && isMobile
      ? classes["line-item-content-price-emphasis-flyout-mobile"]
      : classes[`line-item-content-price-emphasis-${joinClass}`];

  const linkToSlug = item?.slug
    ? `/${currentLocale}/products/${item?.slug}`
    : "#";
  const dispatch = useAppDispatch();

  const dyQtyChangeEventPrevValue = getItem(
    STORAGE_KEYS.DY_QTY_CHANGE_EVENT_PREV_VALUE,
    STORAGE_TYPES.SESSION
  );

  const handleIncrementQuantityPayload = {
    itemQuantity: quantityValue ? quantityValue + 1 : prevValue + 1,
    prevQuantity: prevValue,
    availability: 999,
    lineItems: lineItems,
    sku: item.sku,
    callback: setQuantityValue,
    dispatch: dispatch,
  };

  const decrementQuantityPayload = {
    itemQuantity: quantityValue ?? prevValue,
    lineItems: lineItems,
    sku: item.sku,
    callback: setQuantityValue,
    dispatch: dispatch,
  };

  /**
   * TODO: From Nuwan:
   * prevQuantity value is incorrect, need to do a proper inveatigation and refactoring for the old code
   * For now, I used the session stoarge to store the prevQuantity which is specifically use only for DY
   */
  const handleUpdateQuantityPayload = {
    itemQuantity: quantityValue,
    prevQuantity: prevValue,
    availability: 999,
    lineItems: lineItems,
    sku: item.sku,
    dyQtyChangeEventPrevValue,
    callback: setQuantityValue,
    dispatch: dispatch,
  };

  const handleRemoveItemPayload = {
    lineItems: lineItems,
    sku: item.sku,
    dispatch: dispatch,
  };

  const handleOnChangeQuantity = (value: any) => {
    if (isNaN(Number(value))) {
      setQuantityValue(prevValue);
      return;
    }
    setQuantityValue(Number(value));
    if (value) setPrevValue(Number(value));
  };

  const imageStyle = {
    width: isFlyOut
      ? 124
      : isToast && isMobile && !isMobileSmall
      ? 84
      : isToast && isMobileSmall
      ? 60
      : isCart
      ? 166
      : 87,
    height: isFlyOut
      ? 124
      : isToast && isMobile && !isMobileSmall
      ? 84
      : isToast && isMobileSmall
      ? 60
      : isCart
      ? 166
      : 87,
  };

  const renderUnitQuantity = (quantity: string) => {
    const quantityUnit = t(
      `products.product.quantity_unit.${
        parseInt(quantity) > 1 ? "other" : "one"
      }`,
      { quantity }
    );

    return (
      <span
        className={`${classes["line-item-content-price-note"]} ${
          classes[`line-item-content-price-note-${joinClass}`]
        }`}
      >
        {quantityUnit}
      </span>
    );
  };

  const renderUnitQuantityFixedPrice = (quantity: string) => {
    return quantity
      ? `${quantity} ${parseInt(quantity) > 1 ? "pieces" : "piece"} | ${
          weight * 1000 + " G"
        }`
      : weight
      ? `${weight * 1000 + " G"}`
      : "";
  };

  const generateProductImageUrl = (url?: string) =>
    `${url?.split("?")?.[0]}?fm=webp&w=${imageStyle.width}&h=${
      imageStyle.height
    }`;

  return (
    <div className={`${classes["line-item"]} ${className}`}>
      <div className={classes[`line-item-${joinClass}`]}>
        <div
          className={`${classes["line-item-image"]} ${
            classes[`line-item-image-${joinClass}`]
          }`}
        >
          {item?.productImageUrl ? (
            <>
              <Link href={`/${currentLocale}/products/${item.slug}`}>
                <a>
                  <Image
                    alt={item?.name}
                    layout="fixed"
                    width={imageStyle.width}
                    height={imageStyle.height}
                    src={generateProductImageUrl(item?.productImageUrl)}
                  />
                </a>
              </Link>
              {isReadOnly && (
                <small className={classes["line-item-quantity"]}>
                  {item?.quantity}
                </small>
              )}
            </>
          ) : (
            <Link href={linkToSlug} passHref>
              <div
                className={classes["line-item-image-placeholder"]}
                style={{ width: imageStyle.width + "px" }}
              >
                <span>{`${item?.quantity || 0} ${t(
                  "checkout.label.quantity"
                )}`}</span>
              </div>
            </Link>
          )}
        </div>
        <div
          className={`${classes["line-item-content"]} ${
            classes[`line-item-content-${joinClass}`]
          }`}
        >
          <div className={classes["line-item-content-block"]}>
            <Link href={linkToSlug}>
              <h4
                className={`${classes["line-item-content-title"]} ${
                  classes[`line-item-content-title-${joinClass}`]
                }`}
              >
                {item?.name}
              </h4>
            </Link>
            {isFixedPrice ? (
              <div>
                <span
                  className={`${classes["line-item-content-approx"]} ${
                    classes[`line-item-content-approx-fixed`]
                  }`}
                >
                  {renderUnitQuantityFixedPrice(item?.itemPiecesText)}
                </span>
              </div>
            ) : (
              <div className={classes[`line-item-content-approx-${joinClass}`]}>
                {weightText && (
                  <span
                    className={`${classes["line-item-content-approx"]} ${
                      classes[`line-item-content-approx-${joinClass}`]
                    }`}
                  >
                    {weightText}
                  </span>
                )}
                {(isCart || isFlyOut) &&
                  item?.itemPiecesText &&
                  renderUnitQuantity(item?.itemPiecesText)}
              </div>
            )}
            {!isFixedPrice && item?.perUnitPriceText && !isToast && (
              <>
                <span
                  className={`${classes["line-item-content-price-detail"]} ${
                    classes[`line-item-content-price-detail-${joinClass}`]
                  }`}
                >
                  {unitPrice}
                </span>
                <span className={classes["line-item-content-price-detail"]}>
                  {isCanadianLocale(currentLocale) && unitPriceCA ? "|" : ""}
                </span>
                <span
                  className={`${classes["line-item-content-price-detail"]} ${
                    classes[`line-item-content-price-detail-${joinClass}`]
                  }`}
                >
                  {isCanadianLocale(currentLocale) ? unitPriceCA : ""}
                </span>
              </>
            )}
          </div>
          <div
            className={`${classes["line-item-content-price"]} ${
              classes[`line-item-content-price-${joinClass}`]
            }`}
          >
            <div>
              <span
                className={`${classes["line-item-content-price-emphasis"]} ${priceEmphasisClass}`}
              >
                {currency.format(convertToDollar(totalPrice))}
              </span>

              {!isCart &&
                !isFlyOut &&
                !isToast &&
                item?.itemPiecesText &&
                renderUnitQuantity(item?.itemPiecesText)}
            </div>

            {!isReadOnly && (
              <div
                className={`${classes["line-item-content-wrapper"]} ${
                  classes[`line-item-content-wrapper-${joinClass}`]
                } `}
              >
                <div className={classes["line-item-content-price-qty"]}>
                  <Button
                    className={classes["line-item-content-price-qty-button"]}
                    onClick={() => cartUtilQtyRemove(decrementQuantityPayload)}
                    data-testid="decrement-qty-btn"
                  >
                    {t("cart.buttons.button_decrement_label")}
                  </Button>
                  <TextField
                    color="blue"
                    className={`${
                      classes["line-item-content-price-qty-value"]
                    } ${
                      classes[`line-item-content-price-qty-value-${joinClass}`]
                    }`}
                    value={quantityValue}
                    onBlur={() =>
                      cartUtilQtyOnBlurHandler(handleUpdateQuantityPayload)
                    }
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      handleOnChangeQuantity(e.target.value)
                    }
                    onClick={() =>
                      setItem(
                        STORAGE_KEYS.DY_QTY_CHANGE_EVENT_PREV_VALUE,
                        quantityValue.toString(),
                        STORAGE_TYPES.SESSION
                      )
                    }
                  />

                  <Button
                    onClick={() =>
                      cartUtilQtyUpdate(handleIncrementQuantityPayload)
                    }
                    className={classes["line-item-content-price-qty-button"]}
                    data-testid="increment-qty-btn"
                    disabled={item?.sku === MEMBERSHIP_CONSTANTS.MEMBERSHIP}
                  >
                    {t("cart.buttons.button_increment_label")}
                  </Button>
                </div>
                {isFlyOut && (
                  <p
                    data-testid="action-remove-flyout"
                    onClick={() => cartUtilRemoveItem(handleRemoveItemPayload)}
                    className={`
                      ${classes[`line-item-content-wrapper-${joinClass}`]} ${
                      classes[
                        `line-item-content-wrapper-${joinClass}--clickable`
                      ]
                    }
                    `}
                  >
                    {t("cart.general.remove")}
                  </p>
                )}
              </div>
            )}
          </div>
          {isCart && (
            <div className={classes["line-item-content-price-actions"]}>
              <p
                data-testid="action-remove-cart"
                onClick={() => cartUtilRemoveItem(handleRemoveItemPayload)}
                className={classes["line-item-content-price-actions-remove"]}
              >
                {t("cart.general.remove")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CartLineItem.defaultProps = {
  isCart: false,
  isFlyOut: false,
  className: "",
  isToast: false,
};

export default CartLineItem;
